import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import orderService from "services/orderService";

export const useOrderStatus = (onSuccess, onError) => {
  return useQuery(["order-status"], () => orderService.getStatus(), {
    enabled: false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object({ data: [] }),
    select: response => {
      const data = Object.keys(response).map(key => {
        return { value: key, label: response[key] };
      });

      if (typeof response === "object") {
        return data;
      }

      return response;
    },
  });
};

export const useTransferOrderStatus = (onSuccess, onError) => {
  return useQuery(
    ["order-transfer-status"],
    () => orderService.getTransferOrderStatus(),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        const data = Object.keys(response).map(key => {
          return { value: key, label: response[key] };
        });

        if (typeof response === "object") {
          return data;
        }

        return response;
      },
    }
  );
};

export const useMyOrderList = (payload, onSuccess, onError) => {
  const { params } = payload;

  return useQuery(
    ["my-order-list", params?.page],
    () => orderService.listMyOrders(payload),
    {
      cacheTime: 0,
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return {
          items: response.items || [],
          params: params,
          totalCount: response._meta
            ? response._meta.totalCount
            : response.items.length,
        };
      },
    }
  );
};

export const useListOrders = (payload, onSuccess, onError) => {
  const { params } = payload;

  return useQuery(
    ["order-list", params?.page],
    () => orderService.listOrders(payload),
    {
      cacheTime: 0,
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return {
          items:
            response?.items?.map(o => {
              return {
                ...o,
                isHightLight: o?.is_receive_bus_cost_value_mismatch ? true : false,
              };
            }) || [],
          params: params,
          totalCount: response._meta
            ? response._meta.totalCount
            : response.items.length,
        };
      },
    }
  );
};

export const useListTransferOrders = (payload, onSuccess, onError) => {
  const { params } = payload;

  return useQuery(
    ["transfer-order-list", params?.page],
    () => orderService.listTransferOrders(payload),
    {
      cacheTime: 0,
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return {
          items: response.items || [],
          params: params,
          totalCount: response._meta
            ? response._meta.totalCount
            : response.items.length,
        };
      },
    }
  );
};

export const useListNewSendOrderScanTotal = (payload, onSuccess, onError) => {
  const { params } = payload;

  return useQuery(
    ["new-send-order-scan-total-list", params?.page],
    () => orderService.listNewSendOrderScanTotal(payload),
    {
      cacheTime: 0,
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return {
          items: response.items || [],
          params: params,
          totalCount: response._meta
            ? response._meta.totalCount
            : response.items.length,
        };
      },
    }
  );
};

export const useDetailOrderReminders = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useQuery(
    ["order-detail-reminder"],
    () => orderService.listOrderReminder({ params: payload.orderId }),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_orderDetailAttachments", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );

        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return response;
      },
    }
  );
};

export const useListOrdersActive = (payload, onSuccess, onError) => {
  const { params } = payload;

  return useQuery(
    ["order-list-active", params?.page],
    () => orderService.listOrdersActive(payload),
    {
      cacheTime: 0,
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      // initialData: [],
      select: response => {
        const { data } = response;
        return {
          items:
            data?.items?.map(o => {
              return {
                ...o,
                isHightLight: o?.is_receive_bus_cost_value_mismatch ? true : false,
              };
            }) || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

export const useCancelOrderReasonList = (
  type = undefined,
  onSuccess,
  onError
) => {
  return useQuery(
    ["reason-cancel-order-list"],
    () => orderService.reasonList(type),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: Object({ data: [] }),
      select: response => {
        return response;
      },
    }
  );
};

export const useDetailOrder = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["orders"]);

  return useQuery(["order-detail"], () => orderService.getOrder(payload), {
    enabled: false,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_profile", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    // initialData: Object({ data: {} }),
    select: response => {
      const { data } = response;
      return data;
    },
  });
};

export const useDetailTransferOrder = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["orders"]);
  const { orderId } = payload;
  return useQuery(
    ["transfer-order-detail" + orderId],
    () => orderService.getTransferOrder(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      // initialData: Object({ data: {} }),
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

export const useDeleteOrder = (onSuccess, onError) => {
  return useMutation(orderService.deleteOrder, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useInsertOrderRefCode = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);
  return useMutation(orderService.insertRefCode, {
    retry: 0,
    onSuccess: response => {
      const result = response?.["result"];
      const errors = response?.["errors"];
      if (typeof onSuccess === "function" && result) {
        addToast(
          t("message.update_ref_code", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        onSuccess();
      } else if (!result) {
        addToast(
          t("message.update_ref_code", {
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (errors?.ref_code?.[0]) {
          addToast(errors?.ref_code?.[0], {
            appearance: "error",
            autoDismiss: true,
          });
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useUpdateExistOrderRefCode = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);
  return useMutation(orderService.updateExistRefCode, {
    retry: 0,
    onSuccess: response => {
      const result = response?.["result"];
      const errors = response?.["errors"];
      if (typeof onSuccess === "function" && result) {
        addToast(
          t("message.update_ref_code", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        onSuccess();
      } else if (!result) {
        addToast(
          t("message.update_ref_code", {
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (errors?.ref_code?.[0]) {
          addToast(errors?.ref_code?.[0], {
            appearance: "error",
            autoDismiss: true,
          });
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCancelOrder = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.cancelOrder, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
      if (!!response.result) {
        addToast(
          t("message.cancel", {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          t("message.cancel", {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useAddReceiveBusChild = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.addReceiveBusChild, {
    retry: 0,
    onSuccess: response => {
      const body = response?.data ?? response;

      if (body?.result) {
        addToast(
          t("message.addReceiveBusChild", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          t("message.addReceiveBusChild", {
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess(body);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);

      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.addReceiveBusChild", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useAddChildTransferOrders = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.addChildTransferOrders, {
    retry: 0,
    onSuccess: response => {
      if (response?.result) {
        addToast(
          t("message.addChildTransferOrders", {
            target: t("transferOrder"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          t("message.addChildTransferOrders", {
            target: t("transferOrder"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }
      if (typeof onSuccess === "function") {
        onSuccess(response);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useRemoveChildTransferOrder = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.removeChildTransferOrder, {
    retry: 0,
    onSuccess: response => {
      if (response?.result) {
        addToast(
          t("message.removeChildTransferOrder", {
            target: t("transferOrder"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          response?.message && response?.message !== ""
            ? response?.message
            : t("message.removeChildTransferOrder", {
                target: t("transferOrder"),
                status: t("message.error"),
              }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }
      if (typeof onSuccess === "function") {
        onSuccess(response);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCancelTransferOrder = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.cancelTransferOrder, {
    retry: 0,
    onSuccess: response => {
      if (response?.result) {
        addToast(
          t("message.cancelTransferOrder", {
            target: t("transferOrder"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          t("message.cancelTransferOrder", {
            target: t("transferOrder"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }
      if (typeof onSuccess === "function") {
        onSuccess(response);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCreateOrder = (onSuccess, onError) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  return useMutation(orderService.createOrder, {
    retry: 0,
    onSuccess: response => {
      const data = response;
      addToast(
        t("message.create", {
          target: t("order"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("order"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (errorResponse?.code === 422 && errorResponse?.fields.length > 0) {
        errorResponse.fields.forEach(item => {
          switch (item.field) {
            default:
              addToast(item.message, {
                appearance: "error",
                autoDismiss: true,
                autoDismissTimeout: 15000,
              });
              break;
          }
        });
      }

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCreateTransferOrder = (onSuccess, onError) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  return useMutation(orderService.createTransferOrder, {
    retry: 0,
    onSuccess: response => {
      const data = response;
      addToast(
        t("message.create", {
          target: t("transferOrder"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("transferOrder"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (errorResponse?.code === 422 && errorResponse?.fields.length > 0) {
        errorResponse.fields.forEach(item => {
          switch (item.field) {
            default:
              addToast(item.message, {
                appearance: "error",
                autoDismiss: true,
                autoDismissTimeout: 15000,
              });
              break;
          }
        });
      }

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCopyTransferOrder = (onSuccess, onError) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  return useMutation(orderService.copyTransferOrder, {
    retry: 0,
    onSuccess: response => {
      const data = response;
      if (data.result) {
        addToast(
          t("message.create", {
            target: t("transferOrder"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          formConvertErrorMessage(
            data.message ||
              t("message.create", {
                target: t("transferOrder"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(data.message);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("transferOrder"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (errorResponse?.code === 422 && errorResponse?.fields.length > 0) {
        errorResponse.fields.forEach(item => {
          switch (item.field) {
            default:
              addToast(item.message, {
                appearance: "error",
                autoDismiss: true,
                autoDismissTimeout: 15000,
              });
              break;
          }
        });
      }

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCreateOrderGetGoods = (onSuccess, onError) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  return useMutation(orderService.createOrderGetGoods, {
    retry: 0,
    onSuccess: response => {
      const data = response;
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          t("message.createOrderGetGoods", {
            target: t("order"),
            status: t("message.error"),
          }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (errorResponse?.code === 422 && errorResponse?.fields.length > 0) {
        errorResponse.fields.forEach(item => {
          switch (item.field) {
            default:
              addToast(item.message, {
                appearance: "error",
                autoDismiss: true,
                autoDismissTimeout: 15000,
              });
              break;
          }
        });
      }

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useDetailOrderHistory = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useQuery(
    ["order-detail-history"],
    () => orderService.getOrderHistory(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_orderDetailHistory", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: [],
      select: response => {
        return response;
      },
    }
  );
};

export const useDetailOrderAttachments = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useQuery(
    ["order-detail-attachments"],
    () => orderService.getOrderAttachments(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_orderDetailAttachments", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      initialData: [],
      select: response => {
        return response;
      },
    }
  );
};

export const useTypeOrder = (onSuccess, onError) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  return useQuery(["order-type"], () => orderService.listTypeOrder(), {
    enabled: true,
    retry: 1,
    refetchOnWindowFocus: false,
    onSuccess,
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.fetch_profile", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    initialData: Object({ data: [] }),
    select: response => {
      const data = Object.keys(response).map(key => {
        return { value: key, label: response[key] };
      });

      if (typeof response === "object") {
        return data;
      }

      return response;
    },
  });
};

export const useSearchOrderByCode = (order_code, onSuccess, onError) => {
  return useQuery(
    ["order-search-by-code"],
    () => orderService.searchOrdersByCode(order_code),
    {
      enabled: false,
      retry: 1,
      onSuccess: response => {
        onSuccess(response);
      },
      onError: error => {
        const errorResponse = errorMessage(error);
        // addToast(
        //   formConvertErrorMessage(
        //     errorResponse.message ||
        //       t("message.list", {
        //         target: t("shipping_unit"),
        //         status: t("message.error"),
        //       }),
        //     t
        //   ),
        //   {
        //     appearance: "error",
        //     autoDismiss: true,
        //   }
        // );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        // const orderData = response.items.filter(order => order.record_status !== 'O');
        return {
          order: response.items?.length > 0 ? response.items[0] : null,
        };
      },
    }
  );
};

export const useForwardOrder = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.forwardOrder, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (data?.result) {
        addToast(
          t("message.forward", {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          formConvertErrorMessage(
            t("message.forward", {
              target: t("order"),
              status: t("message.error"),
            }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          t("message.forward", {
            target: t("order"),
            status: t("message.error"),
          }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useForwardOrderMultiple = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.forwardOrderMultiple, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (response?.status === 200) {
        addToast(
          t("message.forward", {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          formConvertErrorMessage(
            t("message.forward", {
              target: t("order"),
              status: t("message.error"),
            }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          t("message.forward", {
            target: t("order"),
            status: t("message.error"),
          }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useExportOrders = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["myOrders"]);
  const excelParams = {
    "columns[delay_time][dimensionOptions][autoSize]": true,
    "columns[delay_time][label]": t("table.time"),

    "columns[type][dimensionOptions][autoSize]": true,
    "columns[type][label]": t("table.type"),

    "columns[order_code][dimensionOptions][autoSize]": true,
    "columns[order_code][label]": t("table.id"),

    "columns[ref_code][dimensionOptions][autoSize]": true,
    "columns[ref_code][label]": t("table.package_code"),

    "columns[status][dimensionOptions][autoSize]": true,
    "columns[status][label]": t("table.status"),

    "columns[sender_name][dimensionOptions][autoSize]": true,
    "columns[sender_name][label]": t("table.customer"),

    "columns[sender_contact_phone][dimensionOptions][autoSize]": true,
    "columns[sender_contact_phone][label]": t("table.SDT"),

    "columns[customer_address][dimensionOptions][autoSize]": true,
    "columns[customer_address][label]": t("table.address"),

    "columns[transport_id][dimensionOptions][autoSize]": true,
    "columns[transport_id][label]": t("table.transport_id"),

    "columns[sale_name][dimensionOptions][autoSize]": true,
    "columns[sale_name][label]": t("table.sale"),

    "columns[timeslot_id][dimensionOptions][autoSize]": true,
    "columns[timeslot_id][label]": t("table.timeslot_id"),
  };
  return useQuery(
    ["order-export"],
    () =>
      orderService.exportOrders({
        params: { ...payload.params },
        excelParams: excelParams,
      }),
    {
      enabled: false,
      retry: 1,
      onSuccess: response => {
        onSuccess(response);
      },
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return response;
      },
    }
  );
};

export const useExportOrdersActive = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["myOrders"]);
  const excelParams = {
    "columns[delay_time][dimensionOptions][autoSize]": true,
    "columns[delay_time][label]": t("table.time"),

    "columns[type][dimensionOptions][autoSize]": true,
    "columns[type][label]": t("table.type"),

    "columns[order_code][dimensionOptions][autoSize]": true,
    "columns[order_code][label]": t("table.id"),

    "columns[ref_code][dimensionOptions][autoSize]": true,
    "columns[ref_code][label]": t("table.package_code"),

    "columns[status][dimensionOptions][autoSize]": true,
    "columns[status][label]": t("table.status"),

    "columns[sender_name][dimensionOptions][autoSize]": true,
    "columns[sender_name][label]": t("table.customer"),

    "columns[sender_contact_phone][dimensionOptions][autoSize]": true,
    "columns[sender_contact_phone][label]": t("table.SDT"),

    "columns[customer_address][dimensionOptions][autoSize]": true,
    "columns[customer_address][label]": t("table.address"),

    "columns[transport_id][dimensionOptions][autoSize]": true,
    "columns[transport_id][label]": t("table.transport_id"),

    "columns[sale_name][dimensionOptions][autoSize]": true,
    "columns[sale_name][label]": t("table.sale"),

    "columns[timeslot_id][dimensionOptions][autoSize]": true,
    "columns[timeslot_id][label]": t("table.timeslot_id"),
  };
  return useQuery(
    ["order-export"],
    () =>
      orderService.exportOrdersActive({
        params: { ...payload.params },
        excelParams: excelParams,
      }),
    {
      enabled: false,
      retry: 1,
      onSuccess: response => {
        onSuccess(response);
      },
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return response;
      },
    }
  );
};

export const useExportMyOrders = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["myOrders"]);
  const excelParams = {
    "columns[delay_time][dimensionOptions][autoSize]": true,
    "columns[delay_time][label]": t("table.time"),

    "columns[type][dimensionOptions][autoSize]": true,
    "columns[type][label]": t("table.type"),

    "columns[order_code][dimensionOptions][autoSize]": true,
    "columns[order_code][label]": t("table.id"),

    "columns[ref_code][dimensionOptions][autoSize]": true,
    "columns[ref_code][label]": t("table.package_code"),

    "columns[status][dimensionOptions][autoSize]": true,
    "columns[status][label]": t("table.status"),

    "columns[sender_name][dimensionOptions][autoSize]": true,
    "columns[sender_name][label]": t("table.customer"),

    "columns[sender_contact_phone][dimensionOptions][autoSize]": true,
    "columns[sender_contact_phone][label]": t("table.SDT"),

    "columns[customer_address][dimensionOptions][autoSize]": true,
    "columns[customer_address][label]": t("table.address"),

    "columns[transport_id][dimensionOptions][autoSize]": true,
    "columns[transport_id][label]": t("table.transport_id"),

    "columns[sale_name][dimensionOptions][autoSize]": true,
    "columns[sale_name][label]": t("table.sale"),

    "columns[timeslot_id][dimensionOptions][autoSize]": true,
    "columns[timeslot_id][label]": t("table.timeslot_id"),
  };
  return useQuery(
    ["order-export"],
    () =>
      orderService.exportMyOrders({
        params: { ...payload.params },
        excelParams: excelParams,
      }),
    {
      enabled: false,
      retry: 1,
      onSuccess: response => {
        onSuccess(response);
      },
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return response;
      },
    }
  );
};

export const useChangeOrderStatus = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.changeOrderStatus, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (data?.result) {
        addToast(
          t("message.change_order_status", {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          formConvertErrorMessage(
            t("message.change_order_status", {
              target: t("order"),
              status: t("message.error"),
            }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.change_order_status", {
              target: t("order"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useChangeOrderStatusMultiple = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.changeOrderStatusMultiple, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (data?.result) {
        addToast(
          t("message.change_order_status", {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      } else {
        addToast(
          formConvertErrorMessage(
            t("message.change_order_status", {
              target: t("order"),
              status: t("message.error"),
            }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.change_order_status", {
              target: t("order"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Change Order Customer
export const useChangeOrderCustomer = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.changeOrderCustomer, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (data?.result) {
        addToast(
          t("message.change_order_customer", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );

        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          formConvertErrorMessage(
            data.message ||
              t("message.change_order_customer", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.change_order_customer", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useListChildOrders = (payload, onSuccess, onError) => {
  const { params, queryKey } = payload;
  return useQuery(
    [queryKey || "order-child-list", params?.page],
    () => orderService.listChildOrders(payload),
    {
      cacheTime: 0,
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return {
          items: response.items || [],
          params: params,
          totalCount: response._meta
            ? response._meta.totalCount
            : response.items.length,
        };
      },
    }
  );
};

// useRemoveOrderChild
export const useRemoveOrderChild = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.removeOrderChild, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (data?.result) {
        addToast(
          t("message.removeOrderChild", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );

        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          formConvertErrorMessage(
            data.message ||
              t("message.removeOrderChild", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.removeOrderChild", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

/**
 * Refund Order
 * @param {MutationProps} val
 */
export const useRefundOrder = ({ onSuccess, onError }) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  return useMutation({
    mutationFn: orderService.refundOrder,
    retry: 1,
    onSuccess: response => {
      /**
       * @type {{
       *  data: {
       *    message?: string;
       *    result?: boolean;
       *  }
       * }}
       */
      const { data } = response;

      if (data?.result === undefined || data?.result === false) {
        addToast(
          t("message.refund_order", {
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
            autoDismissTimeout: 10000,
          }
        );
      } else {
        addToast(
          t("message.refund_order", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.refund_order", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useAttachImageToOrder = (onSuccess, onError) => {
  return useMutation(orderService.attachImageToOrder, {
    retry: 0,
    onSuccess: response => onSuccess(response),
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useListNoteOrderReason = (payload, onSuccess, onError) => {
  return useQuery(
    ["list-note-order-reason"],
    () => orderService.listNoteOrderReason(payload),
    {
      cacheTime: 0,
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return {
          items: response.items || [],
        };
      },
    }
  );
};

export const useNoteOrder = (onSuccess, onError) => {
  const { addToast } = useToasts();
  return useMutation(orderService.noteOrder, {
    retry: 0,
    onSuccess: response => {
      if (response?.result) {
        addToast(response?.message, {
          appearance: "success",
          autoDismiss: true,
        });
        if (typeof onSuccess === "function") {
          onSuccess(response);
        }
      } else {
        addToast("Đặt cảnh báo thất bại", {
          appearance: "error",
          autoDismiss: true,
        });
        if (typeof onError === "function") {
          onError();
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError && onError(errorResponse);
      }
    },
  });
};

export const useGetOrderNotes = (payload, onSuccess, onError) => {
  const { order_id } = payload;

  return useQuery(
    ["order-notes", order_id],
    () => orderService.getOrderNotes({ order_id }),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        return response?.data || response;
      },
    }
  );
};

// useMappingRefCodeUnknownToOrder
export const useMappingRefCodeUnknownToOrder = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["order"]);
  const { addToast } = useToasts();

  return useMutation(orderService.mappingRefCodeUnknownToOrder, {
    retry: 0,
    onSuccess: data => {
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.mappingRefCodeUnknownToOrder", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Change Order Sale
export const useChangeOrderSale = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.changeOrderSale, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;
      if (data?.result) {
        addToast(
          t("message.change_sale", {
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );

        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        const errorMsg =
          typeof data.message === "string"
            ? data.message
            : data.message
            ? String(data.message)
            : t("message.change_sale", {
                status: t("message.error"),
              });

        addToast(formConvertErrorMessage(errorMsg, t), {
          appearance: "error",
          autoDismiss: true,
        });
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.change_sale", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useUpdateOrderCost = (onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderService.updateOrderCost, {
    retry: 0,
    onSuccess: response => {
      addToast(
        t("message.updateOrderCost", {
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );

      if (typeof onSuccess === "function") {
        onSuccess(response);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.updateOrderCost", {
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};
